/**
 * Shared sparkline helpers for AG Grid
 * Provides reusable sparkline configuration with zero-value handling
 */

/* eslint-disable import/prefer-default-export */

/**
 * Creates sparkline cell renderer params with zero-value handling
 *
 * @param {Object} params - AG Grid cell renderer params
 * @param {Object} options - Configuration options
 * @param {string} options.dataField - The field name to get data from (e.g., 'SpendChange', 'SalesChange')
 * @param {string} [options.fill] - Fill color for the sparkline area (default: 'rgba(70, 133, 244, 0.4)')
 * @param {string} [options.stroke] - Stroke color for the sparkline line (default: 'rgb(0, 0, 255)')
 * @returns {Object} Sparkline renderer params with sparklineOptions
 *
 * @example
 * // Usage in AG Grid column definition
 * {
 *   field: 'spendChange',
 *   headerName: 'Spend Change',
 *   cellRenderer: 'agSparklineCellRenderer',
 *   valueGetter: params => {
 *     const includes = params.data?.includes || '';
 *     return includes === 'limitless'
 *       ? params.data?.internalSpendChange
 *       : params.data?.allSpendChange;
 *   },
 *   cellRendererParams: (params) =>
 *     createSparklineCellRendererParams(params, { dataField: 'SpendChange' })
 * }
 */
export const createSparklineCellRendererParams = (params, options = {}) => {
  const {
    dataField,
    fill = 'rgba(70, 133, 244, 0.4)',
    stroke = 'rgb(0, 0, 255)',
  } = options;

  const includes = params.data?.includes || '';

  // Build field names - internal vs all data
  const internalField = `internal${dataField}`;
  const allField = `all${dataField}`;

  const data = includes === 'limitless'
    ? params.data?.[internalField]
    : params.data?.[allField];

  // Calculate max value to determine if all zeros
  const maxValue = data && data.length > 0
    ? Math.max(...data.filter(v => v != null))
    : 0;
  const isAllZeros = maxValue === 0;

  const sparklineOptions = {
    type: 'area',
    fill,
    stroke,
    line: {
      strokeWidth: 1,
    },
    marker: {
      enabled: false,
    },
    highlightStyle: {
      item: {
        fill: 'rgb(143,185,77)',
        strokeWidth: 0,
      },
    },
    axis: {
      type: 'number',
      stroke: 'rgb(204, 204, 235)',
    },
  };

  // If all zeros, add min and max to prevent floating
  // This forces the sparkline to anchor at the bottom of the cell
  if (isAllZeros) {
    sparklineOptions.min = 0;
    sparklineOptions.max = 1;
  }

  return { sparklineOptions };
};
